import cx from "classnames";
import Link from "next/link";
import { useRouter } from "next/router";
import React, { CSSProperties, useEffect, useMemo, useState } from "react";
import { Icons } from "shared/ui";
import styles from "./sidebar.module.scss";

interface ISidebarMenu {
  label: React.ReactNode;
  href: string;
  exact?: boolean;
  additionalHref?: string;
}

interface ISidebarMenuGroup {
  title?: string;
  menu: (ISidebarMenu | false | null | undefined)[];
}

export interface ISidebar {
  mainHref: string;
  menu: (ISidebarMenu | false | null | undefined)[] | (ISidebarMenuGroup | false | null | undefined)[];
  className?: string;
  style?: CSSProperties;
  htmlId?: string;
  sticky?: boolean;
}

export const Sidebar = (props: ISidebar) => {
  const router = useRouter();
  const [activeMenu, setActiveMenu] = useState<ISidebarMenu>();

  const { groups, menu } = useMemo(() => {
    const propsMenu = (props.menu as unknown[]).filter((it) => it) as ISidebarMenu[] | ISidebarMenuGroup[];

    if (!propsMenu.length) return { groups: null, menu: [] };

    if ("menu" in propsMenu[0]) {
      const groups = (propsMenu as ISidebarMenuGroup[]).map((it) => ({
        ...it,
        menu: it.menu.filter((it) => it) as ISidebarMenu[],
      }));
      const menu = groups.map((it) => it.menu).flat();
      return { groups, menu };
    } else {
      return { groups: null, menu: propsMenu as ISidebarMenu[] };
    }
  }, [props.menu]);

  const checkActiveForDescktop = (menuItem: ISidebarMenu) => {
    if (router.asPath.startsWith(menuItem.additionalHref!)) return true;

    switch (router.asPath) {
      case menuItem.href:
        return true;
      case menuItem.additionalHref:
        return true;
      default:
        if (!menuItem.exact) return router.asPath.startsWith(menuItem.href);
    }
  };

  useEffect(() => {
    const activeLink = menu.find((elem) => {
      if (elem.exact) return router.asPath === elem.additionalHref;
      return router.asPath.startsWith(elem.href);
    });
    setActiveMenu(activeLink);
  }, [menu, router]);

  return (
    <div
      className={cx(styles.sidebar, props.sticky && styles.sticky, props.className)}
      style={props.style}
      id={props.htmlId}
    >
      <div className={styles.sidebar_adaptive}>
        {activeMenu ? (
          <Link href={props.mainHref} className={styles.title}>
            <Icons.Down />
            {activeMenu.label}
            <div className={styles.spacer}></div>
          </Link>
        ) : (
          menu.map((it) => (
            <Link
              key={it.href}
              className={styles.adaptive_item}
              href={it.exact ? (it.additionalHref as string) : it.href}
            >
              {it.label}
            </Link>
          ))
        )}
      </div>

      {groups?.map((it, key) => (
        <React.Fragment key={key}>
          {it.title && <div className={styles.group_title}>{it.title}</div>}
          {it.menu.map((it) => {
            const active = checkActiveForDescktop(it);
            return (
              <Link href={it.href} key={it.href} className={cx(styles.item, active && styles.active)}>
                {it.label}
              </Link>
            );
          })}
        </React.Fragment>
      ))}

      {!groups &&
        menu.map((it) => {
          const active = checkActiveForDescktop(it);
          return (
            <Link href={it.href} key={it.href} className={cx(styles.item, active && styles.active)}>
              {it.label}
            </Link>
          );
        })}
    </div>
  );
};

Sidebar.defaultProps = {
  sticky: true,
};
